import { memo, useEffect, useState } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { RedoTimePicker, TimeOfDay } from "./redo-time-picker";

export interface TimeOfDayRange {
  start: TimeOfDay;
  end: TimeOfDay;
}

export const RedoTimeRangePicker = memo(function RedoTimeRangePicker({
  value,
  setValue,
  clearIcon,
}: {
  value: TimeOfDayRange | null;
  setValue: (value: TimeOfDayRange | null) => void;
  clearIcon?: React.ReactNode | null;
}) {
  const [localStartTime, setLocalStartTime] = useState<TimeOfDay | null>(
    value?.start ?? null,
  );
  const [localEndTime, setLocalEndTime] = useState<TimeOfDay | null>(
    value?.end ?? null,
  );

  function handleSetStartRange(time: TimeOfDay | null) {
    if (!time) {
      setLocalStartTime(null);
      return;
    }
    if (localEndTime && isTimeLaterThanTime(time, localEndTime)) {
      setLocalStartTime(localEndTime);
      setLocalEndTime(time);
      return;
    }
    setLocalStartTime(time);
  }

  function handleSetEndRange(time: TimeOfDay | null) {
    if (!time) {
      setLocalEndTime(null);
      return;
    }
    if (localStartTime && isTimeLaterThanTime(localStartTime, time)) {
      setLocalStartTime(time);
      setLocalEndTime(localStartTime);
      return;
    }
    setLocalEndTime(time);
  }

  // Bail out when value changes by reference but not by structure to avoid
  // an infinite update loop caused by callers constructing new objects each render.
  useEffect(() => {
    setLocalStartTime((prev) =>
      isSameTime(prev, value?.start ?? null) ? prev : (value?.start ?? null),
    );
    setLocalEndTime((prev) =>
      isSameTime(prev, value?.end ?? null) ? prev : (value?.end ?? null),
    );
  }, [value]);

  useEffect(() => {
    if (localStartTime && localEndTime) {
      setValue({ start: localStartTime, end: localEndTime });
    } else if (!localStartTime && !localEndTime) {
      setValue(null);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStartTime, localEndTime]);

  return (
    <Flex justify="center">
      <RedoTimePicker
        clearIcon={clearIcon}
        setValue={handleSetStartRange}
        value={localStartTime}
      />
      <Text fontWeight="bold">-</Text>
      <RedoTimePicker
        clearIcon={clearIcon}
        setValue={handleSetEndRange}
        value={localEndTime}
      />
    </Flex>
  );
});

function isTimeLaterThanTime(a: TimeOfDay, b: TimeOfDay): boolean {
  return a.hours > b.hours || (a.hours === b.hours && a.minutes > b.minutes);
}

function isSameTime(a: TimeOfDay | null, b: TimeOfDay | null): boolean {
  if (a === null && b === null) {
    return true;
  }
  if (a === null || b === null) {
    return false;
  }
  return a.hours === b.hours && a.minutes === b.minutes;
}
